import { apiClient } from '@/api/client';
import type {
  MigrationProject,
  MigrationStepDefinition,
  MigrationStepExecution,
  MigrationRecord,
  MigrationManualAction,
  MigrationComment,
  MigrationLog,
  MigrationFilesResponse,
  MigrationReportResponse,
  MigrationReport,
  PaginatedResponse,
  ProjectFilters,
  RecordFilters,
  ManualActionFilters,
  CreateProjectData,
  ResolveActionData,
  BoardData,
  BoardFilters,
  RecordStatus,
  CorrectionSchema,
  AutoCatchUpRun,
  AutoCatchUpSweep,
  AutoCatchUpSweepDetail,
  AutoCatchUpStats,
  AutoCatchUpSchoolSummary,
  AutoCatchUpSchoolDetail,
  AutoCatchUpPagination,
  CollapsedParentRepairReport,
  V1BillingLockdownReport,
  DuplicateUserRepairReport,
} from '../types';

const BASE = '/api/migration';

// --- Helpers ---

function buildParams(filters: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  }
  return params.toString();
}

// --- Projects ---

async function getProjects(filters: ProjectFilters = {}): Promise<PaginatedResponse<MigrationProject>> {
  const response = await apiClient.get<PaginatedResponse<MigrationProject>>(
    `${BASE}/projects?${buildParams(filters)}`
  );
  return response.data;
}

async function getProject(id: number): Promise<MigrationProject> {
  const response = await apiClient.get<MigrationProject>(`${BASE}/projects/${id}`);
  return response.data;
}

async function createProject(data: CreateProjectData): Promise<MigrationProject> {
  const response = await apiClient.post<MigrationProject>(`${BASE}/projects`, data);
  // Pré-mapping IA des champs perso (parents / élèves) lancé en fire-and-forget
  // à la création, pour préparer un maximum de travail. Non bloquant : la
  // création n'attend pas, et l'opérateur peut relancer/corriger via le drawer.
  void applyCustomFieldAiMapping(data.schoolId).catch(() => undefined);
  return response.data;
}

async function deleteProject(id: number): Promise<void> {
  await apiClient.delete(`${BASE}/projects/${id}`);
}

async function analyzeProject(id: number): Promise<MigrationProject> {
  const response = await apiClient.post<MigrationProject>(`${BASE}/projects/${id}/analyze`);
  return response.data;
}

async function runProject(id: number): Promise<MigrationProject> {
  const response = await apiClient.post<MigrationProject>(`${BASE}/projects/${id}/run`);
  return response.data;
}

async function pauseProject(id: number): Promise<MigrationProject> {
  const response = await apiClient.post<MigrationProject>(`${BASE}/projects/${id}/pause`);
  return response.data;
}

async function resumeProject(id: number): Promise<MigrationProject> {
  const response = await apiClient.post<MigrationProject>(`${BASE}/projects/${id}/resume`);
  return response.data;
}

async function resetProject(id: number): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(`${BASE}/projects/${id}/reset`);
  return response.data;
}

/**
 * Répare les familles dont le père et la mère ont fusionné en une seule fiche
 * V2, faute d'avoir deux adresses e-mail distinctes en V1.
 *
 * Sans `apply`, l'appel ne fait que **simuler** : il renvoie ce qui serait
 * corrigé sans rien écrire. C'est ce qui alimente l'aperçu avant confirmation.
 */
async function repairCollapsedParents(
  id: number,
  apply = false
): Promise<CollapsedParentRepairReport> {
  const response = await apiClient.post<CollapsedParentRepairReport>(
    `${BASE}/projects/${id}/repair-collapsed-parents`,
    { apply }
  );
  return response.data;
}

/**
 * Supprime les fiches V2 en double : une tranche de migration tuée après
 * l'écriture V2 et avant l'enregistrement de son résultat a créé les personnes
 * deux fois.
 *
 * Sans `apply`, l'appel ne fait que **simuler** : il renvoie ce qui serait
 * supprimé sans rien écrire.
 */
/**
 * Verrouillage de la facturation/paiement V1 après migration — la seule
 * action de l'outil qui écrit dans la base V1 (exception assumée : sans elle,
 * de nouvelles factures V1 avanceraient la numérotation qui se poursuit en
 * V2). Sans mode : simulation. `apply` : verrouille (idempotent, valeurs
 * d'origine conservées). `restore` : rétablit les valeurs d'origine.
 */
async function v1BillingLockdown(
  id: number,
  mode: 'preview' | 'apply' | 'restore' = 'preview'
): Promise<V1BillingLockdownReport> {
  const body = mode === 'apply' ? { apply: true } : mode === 'restore' ? { restore: true } : {};
  const response = await apiClient.post<V1BillingLockdownReport>(
    `${BASE}/projects/${id}/v1-billing-lockdown`,
    body
  );
  return response.data;
}

async function repairDuplicateUsers(
  id: number,
  apply = false
): Promise<DuplicateUserRepairReport> {
  const response = await apiClient.post<DuplicateUserRepairReport>(
    `${BASE}/projects/${id}/repair-duplicate-users`,
    { apply }
  );
  return response.data;
}

/**
 * « Recommencer de zéro » : purge tous les contenus de migration ADM du projet
 * ET réinitialise le tenant V2 de l'école (soft-delete + nouvel UUID au
 * redéploiement). Action destructrice — permission `action.migration_v1_v2.reset`.
 */
async function restartFromScratch(
  id: number
): Promise<{ message: string; v2Deleted: boolean; v2Error: string | null }> {
  const response = await apiClient.post<{ message: string; v2Deleted: boolean; v2Error: string | null }>(
    `${BASE}/projects/${id}/restart`
  );
  return response.data;
}

// --- Step Definitions ---

async function getStepDefinitions(): Promise<MigrationStepDefinition[]> {
  const response = await apiClient.get<MigrationStepDefinition[]>(`${BASE}/step-definitions`);
  return response.data;
}

// --- Step Executions ---

async function getStepExecution(id: number): Promise<MigrationStepExecution> {
  const response = await apiClient.get<MigrationStepExecution>(`${BASE}/step-executions/${id}`);
  return response.data;
}

async function updateStepExecutionMapping(
  id: number,
  fieldMapping: Record<string, unknown>
): Promise<MigrationStepExecution> {
  const response = await apiClient.put<MigrationStepExecution>(
    `${BASE}/step-executions/${id}/mapping`,
    { fieldMapping }
  );
  return response.data;
}

async function runNextStep(projectId: number): Promise<MigrationStepExecution | null> {
  const response = await apiClient.post<MigrationStepExecution | ''>(
    `${BASE}/projects/${projectId}/run-next`
  );
  if (response.status === 204 || response.data === '') return null;
  return response.data as MigrationStepExecution;
}

async function runAllSteps(
  projectId: number
): Promise<{ executed: MigrationStepExecution[]; count: number }> {
  const response = await apiClient.post<{ executed: MigrationStepExecution[]; count: number }>(
    `${BASE}/projects/${projectId}/run-all`
  );
  return response.data;
}

export interface RunChunkResult {
  step: MigrationStepExecution;
  done: boolean;
  processed: number;
  remaining: number;
}

async function runStepChunk(
  id: number,
  limit = 10,
  reconcile = false
): Promise<RunChunkResult> {
  const response = await apiClient.post<RunChunkResult>(
    `${BASE}/step-executions/${id}/run-chunk?limit=${limit}${reconcile ? '&reconcile=1' : ''}`,
    // Un chunk lourd (ex. 10 parents = géocodage + create + bind côté V2) peut
    // être long. On coupe court AVANT le timeout de la gateway (~60 s) pour
    // reprendre la main proprement : le serveur, lui, continue sous verrou, et
    // le retry côté appelant reprend là où il en est (idempotent). Sans ce
    // timeout, un chunk bloqué laisserait la requête pendre jusqu'à ce que la
    // gateway coupe la connexion → « Network Error » non rattrapable.
    { timeout: 55000 }
  );
  return response.data;
}

/**
 * Un `run-chunk` peut échouer de façon TRANSITOIRE sur les gros volumes, sans
 * que la donnée soit en cause :
 *  - « Network Error » / timeout : aucune réponse HTTP — la gateway a coupé une
 *    requête trop longue (le serveur, lui, POURSUIT le traitement sous verrou) ;
 *  - 502 / 503 / 504 : indisponibilité passagère de l'ingress/upstream ;
 *  - 400 « un traitement est déjà en cours » : le verrou serveur (GET_LOCK 55 s)
 *    est encore tenu par le chunk précédent qui finit de s'exécuter.
 *
 * Ces cas sont rejouables : le verrou MySQL sérialise les process et la reprise
 * saute les records déjà migrés. On réessaie donc avec backoff plafonné ; seule
 * une vraie erreur métier (400 autre, 403, 404…) remonte immédiatement.
 *
 * ⚠️ Le verrou règle la CONCURRENCE, pas la DURABILITÉ : une requête tuée par la
 * gateway peut avoir écrit côté V2 sans que le serveur ait eu le temps
 * d'enregistrer ce qu'il venait de créer. C'est ce qui a dupliqué des élèves en
 * août 2026. L'absence de double-création tient désormais à deux gardes côté
 * serveur — `TargetCheckpointer` (enregistrement immédiat de l'id V2) et
 * `OrphanSchoolUserFinder` (la relance adopte la fiche orpheline) — et non au
 * verrou.
 */
const CHUNK_MAX_RETRIES = 8;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function isTransientChunkError(err: unknown): boolean {
  const e = err as {
    response?: { status?: number; data?: { error?: string } };
    code?: string;
  };
  // Aucune réponse : coupure réseau ou timeout (axios ou gateway). Le serveur
  // peut très bien avoir continué le traitement de son côté.
  if (!e.response) return true;
  const status = e.response.status;
  if (status === 502 || status === 503 || status === 504) return true;
  // Verrou serveur encore tenu par le chunk précédent (traitement en cours).
  if (status === 400 && /traitement.*en cours/i.test(e.response.data?.error ?? '')) {
    return true;
  }
  return false;
}

/**
 * Enveloppe résiliente de {@link runStepChunk} : réessaie les échecs transitoires
 * (cf. {@link isTransientChunkError}) avec un backoff exponentiel plafonné à 30 s.
 * C'est ce qui permet aux gros lots (parents, élèves…) d'aller au bout sans qu'un
 * « Network Error » ponctuel n'avorte toute la migration du lot.
 */
async function runStepChunkResilient(
  id: number,
  limit = 10,
  reconcile = false
): Promise<RunChunkResult> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= CHUNK_MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      // 2s, 4s, 8s, 16s, puis 30s : laisse le temps au verrou serveur de se
      // libérer et à la gateway de récupérer avant de retenter.
      await sleep(Math.min(30000, 2000 * 2 ** (attempt - 1)));
    }
    try {
      return await runStepChunk(id, limit, reconcile);
    } catch (err) {
      lastErr = err;
      if (!isTransientChunkError(err)) throw err;
    }
  }
  throw lastErr;
}

/**
 * Lance (ou relance) un lot.
 *
 * @param reconcile lots réconciliants uniquement (ex. `school.student_groups`) :
 *   ré-ouvre les records déjà migrés pour rejouer le diff additif sur
 *   l'existant — sert à rattacher des membres manqués sans reset du projet.
 */
async function runStepExecution(
  id: number,
  reconcile = false
): Promise<MigrationStepExecution> {
  const response = await apiClient.post<MigrationStepExecution>(
    `${BASE}/step-executions/${id}/run${reconcile ? '?reconcile=1' : ''}`
  );
  return response.data;
}

// --- Emplois du temps (lot school.schedules — « annule et remplace ») ---

export interface SchedulesV2Status {
  agendaId: string | null;
  agendaName: string | null;
  existingSlots: number;
  /** Créneaux V1 déjà migrés par ce lot (records `migrated`). */
  migratedRecords?: number;
  /**
   * Les créneaux V2 sont étrangers à ce lot (aucun record migré) : re-migrer
   * dupliquerait l'emploi du temps, il faut donc proposer l'écrasement. Faux
   * sur une reprise — le run repart des records non migrés.
   */
  needsReplace?: boolean;
}

/**
 * État de l'emploi du temps V2 (agenda actif + créneaux existants + créneaux
 * déjà migrés par le lot). Sert au warning « annule et remplace » avant de
 * lancer le lot des créneaux.
 */
async function getSchedulesV2Status(stepId: number): Promise<SchedulesV2Status> {
  const response = await apiClient.get<SchedulesV2Status>(
    `${BASE}/step-executions/${stepId}/schedules/v2-status`
  );
  return response.data;
}

/**
 * Écrase l'emploi du temps V2 : soft-delete de tous les créneaux de l'agenda
 * actif + purge des records du lot (re-migration complète au prochain run).
 */
async function purgeSchedulesV2(
  stepId: number
): Promise<{ deletedSlots: number; recordsPurged: number; agendaId: string | null }> {
  const response = await apiClient.post<{
    deletedSlots: number;
    recordsPurged: number;
    agendaId: string | null;
  }>(`${BASE}/step-executions/${stepId}/schedules/v2-purge`);
  return response.data;
}

// --- Jobs field-values (import asynchrone V2 des champs perso/natifs) ---

export interface FieldValueJobFailedItem {
  userRoleId: string;
  /** id V1 du record concerné (rattachement au lot) — null si introuvable. */
  sourceId: string | null;
  status: string;
  errors: Array<{ fieldId: string | null; code: string; message: string }>;
}

export interface FieldValueJob {
  id: number;
  v2JobId: string;
  status: 'pending' | 'in_progress' | 'completed' | 'completed_with_errors' | 'failed';
  totalSubmitted: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  failedItems: FieldValueJobFailedItem[];
  trackingError: string | null;
  submittedAt: string | null;
  lastCheckedAt: string | null;
  completedAt: string | null;
}

export interface FieldValueJobsResponse {
  jobs: FieldValueJob[];
  /**
   * Compteurs DÉDUPLIQUÉS par utilisateur : un userRole rejoué (bouton
   * « Relancer les échecs ») compte une seule fois, avec le statut de son job
   * le plus récent. `failedItems` = échecs encore d'actualité uniquement.
   */
  aggregate: {
    /** `idle` = aucun job soumis pour ce lot. */
    status: 'idle' | 'in_progress' | 'completed' | 'completed_with_errors';
    totalSubmitted: number;
    successCount: number;
    failedCount: number;
    pendingCount: number;
    failedItems: FieldValueJobFailedItem[];
  };
}

/**
 * Avancement des jobs V2 d'import ASYNCHRONE des valeurs de champs
 * (perso/natifs) soumis pendant un lot Utilisateurs. Le back rafraîchit les
 * jobs non terminés auprès de V2 à chaque appel — le polling front suffit.
 */
async function getFieldValueJobs(stepId: number): Promise<FieldValueJobsResponse> {
  const response = await apiClient.get<FieldValueJobsResponse>(
    `${BASE}/step-executions/${stepId}/field-value-jobs`
  );
  return response.data;
}

/**
 * Rejoue les utilisateurs encore en échec : chaque record source est relancé
 * via le chemin unitaire du lot (idempotent), qui soumet un nouveau job batch.
 * Peut être long (N relances séquentielles côté serveur) — timeout étendu.
 */
async function retryFieldValueJobs(
  stepId: number
): Promise<{ retried: number; failed: number; errors: string[] }> {
  const response = await apiClient.post<{ retried: number; failed: number; errors: string[] }>(
    `${BASE}/step-executions/${stepId}/field-value-jobs/retry`,
    undefined,
    { timeout: 120000 }
  );
  return response.data;
}

/**
 * Saute volontairement un lot (statut `skipped`) — typiquement un lot bloqué
 * par ses dépendances que l'opérateur choisit de traiter plus tard.
 */
async function skipStep(id: number): Promise<MigrationStepExecution> {
  const response = await apiClient.post<MigrationStepExecution>(
    `${BASE}/step-executions/${id}/skip`
  );
  return response.data;
}

/**
 * Contourne volontairement un lot incomplet (statut `bypassed`) : contrairement
 * à `skipped`, ce statut SATISFAIT les dépendances des lots suivants (y compris
 * strictes). Le lot reste relançable plus tard.
 */
async function bypassStep(id: number): Promise<MigrationStepExecution> {
  const response = await apiClient.post<MigrationStepExecution>(
    `${BASE}/step-executions/${id}/bypass`
  );
  return response.data;
}

/**
 * Estime le volume d'un seul lot (COUNT V1 sur la sourceQuery du lot).
 * Idempotent — peut être appelé plusieurs fois sans effet de bord.
 */
async function refreshStepCount(id: number): Promise<MigrationStepExecution> {
  const response = await apiClient.post<MigrationStepExecution>(
    `${BASE}/step-executions/${id}/refresh-count`
  );
  return response.data;
}

// --- Records ---

async function getRecords(
  stepExecutionId: number,
  filters: RecordFilters = {}
): Promise<PaginatedResponse<MigrationRecord>> {
  const response = await apiClient.get<PaginatedResponse<MigrationRecord>>(
    `${BASE}/step-executions/${stepExecutionId}/records?${buildParams(filters)}`
  );
  return response.data;
}

async function retryRecord(recordId: number): Promise<MigrationRecord> {
  const response = await apiClient.post<MigrationRecord>(`${BASE}/records/${recordId}/retry`);
  return response.data;
}

/**
 * Édition manuelle du mappedData d'un record (correction d'une ligne en échec
 * avant relance unitaire). Merge superficiel : seules les clés fournies sont
 * écrites, `null` supprime la clé. Refusé sur un record déjà migré.
 */
async function updateRecordMappedData(
  recordId: number,
  mappedData: Record<string, unknown>
): Promise<MigrationRecord> {
  const response = await apiClient.patch<MigrationRecord>(
    `${BASE}/records/${recordId}/mapped-data`,
    { mappedData }
  );
  return response.data;
}

/**
 * Exclut un record de la migration (raison obligatoire, affichée dans le
 * visualiseur et reprise dans le rapport). Ni succès ni échec : compte comme
 * traité, ne bloque pas le lot, survit aux relances. Refusé sur un record
 * déjà migré.
 */
async function excludeRecord(recordId: number, reason: string): Promise<MigrationRecord> {
  const response = await apiClient.post<MigrationRecord>(
    `${BASE}/records/${recordId}/exclude`,
    { reason }
  );
  return response.data;
}

/** Ré-inclut un record exclu : il repasse « en attente » et sera repris. */
async function includeRecord(recordId: number): Promise<MigrationRecord> {
  const response = await apiClient.post<MigrationRecord>(`${BASE}/records/${recordId}/include`);
  return response.data;
}

/**
 * Schéma de correction d'un record : champs corrigeables décrits par un widget
 * lisible (texte, nombre, liste déroulante…) et, pour les champs référence
 * (enseignant, salle, niveau), les options {value, label} résolues depuis le
 * lot correspondant. Alimente le formulaire de correction convivial.
 */
async function getRecordCorrectionSchema(recordId: number): Promise<CorrectionSchema> {
  const response = await apiClient.get<CorrectionSchema>(
    `${BASE}/records/${recordId}/correction-schema`
  );
  return response.data;
}

// --- Fichiers migrés (lots « fichiers » : photos, logo, PJ, GED) ---

/**
 * Liste paginée des fichiers d'un lot « fichiers » avec URL des deux côtés.
 * `verify=true` déclenche la vérification serveur (présence + taille V1/V2,
 * verdict par fichier) — plus lent, à réserver à l'action explicite.
 */
async function getStepFiles(
  stepExecutionId: number,
  opts: { page?: number; limit?: number; status?: RecordStatus | ''; verify?: boolean } = {}
): Promise<MigrationFilesResponse> {
  const params = new URLSearchParams();
  if (opts.page) params.set('page', String(opts.page));
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.status) params.set('status', opts.status);
  if (opts.verify) params.set('verify', '1');
  const response = await apiClient.get<MigrationFilesResponse>(
    `${BASE}/step-executions/${stepExecutionId}/files?${params.toString()}`
  );
  return response.data;
}

/**
 * Charge l'aperçu V2 d'un record fichier (proxy ADM du download V2 — les
 * fichiers migrés sont PRIVATE, pas d'URL directe possible) et renvoie un
 * object URL utilisable en <img src> / <a href>. À révoquer après usage.
 */
async function fetchRecordV2FileObjectUrl(recordId: number): Promise<string> {
  const response = await apiClient.get<Blob>(`${BASE}/records/${recordId}/file/v2`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
}

// --- Rapport d'audit de migration ---

/**
 * Dernier rapport généré + snapshot « live » recalculé à chaque appel.
 * Les tableaux de l'UI se basent toujours sur `current.snapshot` (à jour au
 * fil de l'avancement) ; l'analyse rédigée vient de `report.aiAnalysis` et
 * est marquée obsolète quand `report.dataHash !== current.dataHash`.
 */
async function getProjectReport(projectId: number): Promise<MigrationReportResponse> {
  const response = await apiClient.get<MigrationReportResponse>(`${BASE}/projects/${projectId}/report`);
  return response.data;
}

/** Génère (synchrone) un nouveau rapport : snapshot + analyse Gemini. */
async function generateProjectReport(
  projectId: number
): Promise<{ report: MigrationReport; aiEnabled: boolean }> {
  const response = await apiClient.post<{ report: MigrationReport; aiEnabled: boolean }>(
    `${BASE}/projects/${projectId}/report/generate`
  );
  return response.data;
}

// --- Manual Actions ---

async function getManualActions(
  projectId: number,
  filters: ManualActionFilters = {}
): Promise<PaginatedResponse<MigrationManualAction>> {
  const response = await apiClient.get<PaginatedResponse<MigrationManualAction>>(
    `${BASE}/projects/${projectId}/manual-actions?${buildParams(filters)}`
  );
  return response.data;
}

async function resolveAction(actionId: number, data: ResolveActionData): Promise<MigrationManualAction> {
  const response = await apiClient.post<MigrationManualAction>(
    `${BASE}/manual-actions/${actionId}/resolve`,
    data
  );
  return response.data;
}

async function dismissAction(actionId: number): Promise<MigrationManualAction> {
  const response = await apiClient.post<MigrationManualAction>(
    `${BASE}/manual-actions/${actionId}/dismiss`
  );
  return response.data;
}

// --- Comments (fil de discussion projet) ---

async function getComments(projectId: number): Promise<MigrationComment[]> {
  const response = await apiClient.get<{ data: MigrationComment[] }>(
    `${BASE}/projects/${projectId}/comments`
  );
  return response.data.data;
}

async function createComment(projectId: number, body: string): Promise<MigrationComment> {
  const response = await apiClient.post<MigrationComment>(
    `${BASE}/projects/${projectId}/comments`,
    { body }
  );
  return response.data;
}

async function deleteComment(commentId: number): Promise<void> {
  await apiClient.delete(`${BASE}/comments/${commentId}`);
}

// --- Logs ---

async function getLogs(projectId: number, page = 1, limit = 50): Promise<PaginatedResponse<MigrationLog>> {
  const response = await apiClient.get<PaginatedResponse<MigrationLog>>(
    `${BASE}/projects/${projectId}/logs?page=${page}&limit=${limit}`
  );
  return response.data;
}

// --- Project config (school years) ---

export interface SourceSchoolYearEntry {
  id: number;
  label: string;
  title: string | null;
  startingDate: string | null;
  endingDate: string | null;
  showInForm: boolean;
}

export interface TargetSchoolYearEntry {
  id: string | null;
  label: string | null;
  startingDate: string | null;
  endingDate: string | null;
  status: string | null;
}

export interface UpdateProjectConfigInput {
  sourceSchoolYearV1Id: number | null;
  sourceSchoolYearV1Label: string | null;
  targetSchoolYearV2Id: string | null;
  targetSchoolYearV2Label: string | null;
}

async function getSourceSchoolYears(projectId: number): Promise<SourceSchoolYearEntry[]> {
  const response = await apiClient.get<{ data: SourceSchoolYearEntry[] }>(
    `${BASE}/projects/${projectId}/source-years`
  );
  if (!Array.isArray(response.data?.data)) {
    throw new Error('Réponse inattendue du serveur (années V1 illisibles)');
  }
  return response.data.data;
}

async function getTargetSchoolYears(projectId: number): Promise<TargetSchoolYearEntry[]> {
  const response = await apiClient.get<{ data: TargetSchoolYearEntry[] }>(
    `${BASE}/projects/${projectId}/target-years`
  );
  if (!Array.isArray(response.data?.data)) {
    throw new Error(
      'Réponse inattendue du tenant V2 — il est probablement supprimé ou pas encore redéployé'
    );
  }
  return response.data.data;
}

async function updateProjectConfig(
  projectId: number,
  data: UpdateProjectConfigInput
): Promise<MigrationProject> {
  const response = await apiClient.put<MigrationProject>(
    `${BASE}/projects/${projectId}/config`,
    data
  );
  return response.data;
}

async function refreshProjectCounts(
  projectId: number
): Promise<{ data: MigrationProject; warnings?: string[] }> {
  const response = await apiClient.post<{ data: MigrationProject; warnings?: string[] }>(
    `${BASE}/projects/${projectId}/refresh-counts`
  );
  return response.data;
}

/**
 * Recompte la volumétrie V1 live des lots utilisateurs (élèves, parents,
 * staff, photos), y compris ceux déjà terminés, et en déduit les ajouts V1
 * non encore migrés. Appelé automatiquement à l'ouverture d'un dossier.
 * Lecture seule côté V1 ; tolérant (renvoie 200 + warnings si V1 injoignable).
 */
async function refreshProjectSourceCounts(
  projectId: number
): Promise<{ data: MigrationProject; warnings?: string[] }> {
  const response = await apiClient.post<{ data: MigrationProject; warnings?: string[] }>(
    `${BASE}/projects/${projectId}/source-counts`
  );
  return response.data;
}

// --- Rattrapage automatique des utilisateurs ---

/** Historique des passes de rattrapage automatique (la plus récente en tête). */
async function getProjectAutoCatchUps(
  projectId: number
): Promise<{ data: AutoCatchUpRun[]; eligible: boolean }> {
  const response = await apiClient.get<{ data: AutoCatchUpRun[]; eligible: boolean }>(
    `${BASE}/projects/${projectId}/auto-catch-up`
  );
  return response.data;
}

/**
 * Déclenche immédiatement le rattrapage : les utilisateurs inscrits en V1
 * depuis la migration sont créés en V2 puis rattachés à leur classe et à leurs
 * groupes. Aucune donnée existante n'est modifiée. `data` vaut null quand la V1
 * n'a rien de neuf. Peut être long (création unitaire côté V2).
 */
async function runProjectAutoCatchUp(
  projectId: number
): Promise<{ data: AutoCatchUpRun | null; project: MigrationProject }> {
  const response = await apiClient.post<{ data: AutoCatchUpRun | null; project: MigrationProject }>(
    `${BASE}/projects/${projectId}/auto-catch-up`,
    undefined,
    // Une passe enchaîne plusieurs lots (dont les photos) : le défaut Axios
    // couperait avant la fin côté serveur.
    { timeout: 600_000 }
  );
  return response.data;
}

export interface BirthPlaceBackfillResult {
  dryRun: boolean;
  eligible: number;
  candidates: number;
  nothingToImport: number;
  /** Élèves dont la nomenclature n'a pas répondu : à relancer, donnée non absente. */
  lookupFailures: number;
  written: number;
  untouched: number;
  failed: number;
  samples: Array<{
    eleve: string;
    communeCode: string | null;
    countryCode: string | null;
    lieu: string | null;
    lookupFailed: boolean;
  }>;
  errors: string[];
}

/**
 * Rattrape le lieu de naissance des élèves déjà migrés : la commune et le pays
 * n'ont rejoint le catalogue des champs natifs qu'après la bascule de la
 * plupart des écoles. Additif — un élève qui porte déjà un lieu de naissance
 * n'est pas touché.
 *
 * `dryRun` ne fait AUCUN appel à la V2 : il lit la V1 et annonce ce qui serait
 * écrit. L'exécution réelle peut être longue (écriture unitaire côté V2).
 */
async function runProjectBirthPlaceBackfill(
  projectId: number,
  dryRun: boolean
): Promise<BirthPlaceBackfillResult> {
  const response = await apiClient.post<BirthPlaceBackfillResult>(
    `${BASE}/projects/${projectId}/backfill-birth-places?dryRun=${dryRun ? 1 : 0}`,
    undefined,
    { timeout: 900_000 }
  );
  return response.data;
}

/**
 * Liste des passes (exécutions de la tâche programmée), la plus récente en
 * tête — y compris celles qui n'ont rien eu à reprendre. Les simulations
 * (`--dry-run`) sont exclues sauf `includeDryRun`.
 */
async function getAutoCatchUpSweeps(params: {
  page?: number;
  perPage?: number;
  trigger?: string;
  status?: string;
  onlyWithAdditions?: boolean;
  includeDryRun?: boolean;
} = {}): Promise<{
  data: AutoCatchUpSweep[];
  pagination: AutoCatchUpPagination;
  stats: AutoCatchUpStats;
}> {
  const query = buildParams(params);
  const response = await apiClient.get<{
    data: AutoCatchUpSweep[];
    pagination: AutoCatchUpPagination;
    stats: AutoCatchUpStats;
  }>(`${BASE}/auto-catch-up/sweeps${query ? `?${query}` : ''}`);
  return response.data;
}

/** Détail d'une passe : chaque école balayée et ce qui y a été repris. */
async function getAutoCatchUpSweep(sweepId: number): Promise<AutoCatchUpSweepDetail> {
  const response = await apiClient.get<{ data: AutoCatchUpSweepDetail }>(
    `${BASE}/auto-catch-up/sweeps/${sweepId}`
  );
  return response.data.data;
}

/** Vue croisée : ce que le rattrapage a repris école par école. */
async function getAutoCatchUpSchools(params: {
  page?: number;
  perPage?: number;
  search?: string;
  sort?: 'last' | 'added' | 'school';
} = {}): Promise<{ data: AutoCatchUpSchoolSummary[]; pagination: AutoCatchUpPagination }> {
  const query = buildParams(params);
  const response = await apiClient.get<{
    data: AutoCatchUpSchoolSummary[];
    pagination: AutoCatchUpPagination;
  }>(`${BASE}/auto-catch-up/schools${query ? `?${query}` : ''}`);
  return response.data;
}

/** Historique de rattrapage d'un dossier (passes, lots, personnes ajoutées). */
async function getAutoCatchUpSchool(projectId: number): Promise<AutoCatchUpSchoolDetail> {
  const response = await apiClient.get<{ data: AutoCatchUpSchoolDetail }>(
    `${BASE}/auto-catch-up/schools/${projectId}`
  );
  return response.data.data;
}

// --- Schools ---

export interface MigrationSchoolEntry {
  id: number;
  code: string;
  name: string;
  status: string;
  version: string;
  productionDate: string | null;
  migrationProject: {
    id: number;
    status: string;
    totalRecords: number;
    processedRecords: number;
    failedRecords: number;
    startedAt: string | null;
    completedAt: string | null;
    createdAt: string;
    assignedTo: { id: number; firstname: string; lastname: string } | null;
  } | null;
}

export interface SchoolFilters {
  search?: string;
  migrationStatus?: string;
  /** Filtre par responsable assigné (admin uniquement). 'all' = pas de filtre. */
  ownerId?: number | 'all';
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

async function getSchools(filters: SchoolFilters = {}): Promise<PaginatedResponse<MigrationSchoolEntry>> {
  const response = await apiClient.get<PaginatedResponse<MigrationSchoolEntry>>(
    `${BASE}/schools?${buildParams(filters)}`
  );
  return response.data;
}

// --- Board (kanban) ---

async function getBoard(filters: BoardFilters = {}): Promise<BoardData> {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.ownerId !== undefined && filters.ownerId !== 'all') {
    params.set('ownerId', String(filters.ownerId));
  }
  if (filters.plannedFrom) params.set('plannedFrom', filters.plannedFrom);
  if (filters.plannedTo) params.set('plannedTo', filters.plannedTo);
  const response = await apiClient.get<BoardData>(`${BASE}/board?${params.toString()}`);
  return response.data;
}

async function assignProject(projectId: number, userId: number | null): Promise<MigrationProject> {
  const response = await apiClient.put<MigrationProject>(`${BASE}/projects/${projectId}/assign`, {
    userId,
  });
  return response.data;
}

/**
 * Définit (ou efface avec `null`) la date de migration prévue d'un projet.
 * Réservé aux administrateurs. `date` au format AAAA-MM-JJ.
 */
async function updateProjectPlannedDate(
  projectId: number,
  date: string | null
): Promise<MigrationProject> {
  const response = await apiClient.put<MigrationProject>(
    `${BASE}/projects/${projectId}/planned-date`,
    { plannedMigrationDate: date }
  );
  return response.data;
}

/** Utilisateur back-office pouvant se voir assigner un projet de migration. */
export interface AssignableUser {
  id: number;
  firstname: string;
  lastname: string;
  email: string | null;
}

/**
 * Cache mémoire (durée de session) de la liste des utilisateurs assignables.
 * Cette liste est stable et réutilisée par chaque ligne de la liste des écoles
 * ainsi que par la page détail / le board — on évite donc les re-fetch et les
 * appels concurrents identiques. Endpoint réservé aux admins (403 sinon).
 */
let assignableUsersCache: AssignableUser[] | null = null;
let assignableUsersInflight: Promise<AssignableUser[]> | null = null;

async function getAssignableUsers(): Promise<AssignableUser[]> {
  if (assignableUsersCache) return assignableUsersCache;
  if (assignableUsersInflight) return assignableUsersInflight;

  assignableUsersInflight = apiClient
    .get<AssignableUser[]>(`${BASE}/assignable-users`)
    .then((response) => {
      assignableUsersCache = response.data;
      return response.data;
    })
    .finally(() => {
      assignableUsersInflight = null;
    });

  return assignableUsersInflight;
}

// --- Config / Referential mappings ---

export interface ReferentialMapping {
  id?: number;
  type: string;
  sourceId: string;
  sourceLabel: string;
  targetCode: string | null;
  targetLabel: string | null;
  /** Attribut V2 EXISTANT choisi comme cible (custom_field). Null = créer/réutiliser par nom. */
  targetId?: string | null;
  /** Code Éducation Nationale : MEF (niveau) ou code BCN (matière). Écoles EN. */
  enCode?: string | null;
}

/** Option EN (MEF ou matière) pour les sélecteurs de mapping niveau→MEF / matière→BCN. */
export interface EnCodeOption {
  code: string;
  label: string;
  isLangueVivante?: boolean;
  rang?: number | null;
  typeLangueRequis?: string[];
  surmatiereLibelle?: string | null;
  /** Langue concrète déclinée d'une surmatière LV générique (ex. « Allemand »). */
  langueLibelle?: string | null;
}

async function getMappings(type: string): Promise<ReferentialMapping[]> {
  const response = await apiClient.get<ReferentialMapping[]>(`${BASE}/config/mappings/${type}`);
  return response.data;
}

async function saveMappings(
  type: string,
  mappings: ReferentialMapping[],
  schoolId?: number
): Promise<{ message: string; count: number; mappings: ReferentialMapping[] }> {
  const qs = schoolId !== undefined ? `?schoolId=${schoolId}` : '';
  const response = await apiClient.put<{ message: string; count: number; mappings: ReferentialMapping[] }>(
    `${BASE}/config/mappings/${type}${qs}`,
    { mappings }
  );
  return response.data;
}

// --- Custom fields (V1 customfields → V2 AttributeType) ---

export interface CustomFieldRow {
  sourceId: string;
  sourceLabel: string;
  sourceType: string;
  sourceRequired: boolean;
  sourcePriority: number;
  sourceDescription: string | null;
  targetCode: string | null;
  targetLabel: string | null;
  /** Attribut V2 existant choisi explicitement (null sinon). */
  targetId?: string | null;
  hasMapping: boolean;
  /** Présent seulement dans la vue « v2-preview ». */
  verdict?: 'reuse-explicit' | 'reuse-name' | 'create';
  matchedAttributeId?: string | null;
  matchedAttributeName?: string | null;
}

export interface CustomFieldListResponse {
  form: 1 | 2;
  total: number;
  mapped: number;
  fields: CustomFieldRow[];
}

/** Attribut V2 existant proposé dans le sélecteur du drawer. */
export interface V2AttributeOption {
  id: string;
  name: string;
  code: string;
  type: string | null;
}

export interface CustomFieldDrawerResponse {
  form: 1 | 2;
  total: number;
  willCreate: number;
  willReuse: number;
  v2Attributes: V2AttributeOption[];
  fields: CustomFieldRow[];
}

/**
 * Liste les `customfields` V1 d'un formulaire (1 = élèves, 2 = parents)
 * pour une école donnée, avec leur mapping V2 actuel. Utilisé par le
 * drawer « Champs perso » des lots `parents.fathers`, `parents.mothers`
 * et `students.eleves`.
 */
async function getCustomFields(schoolId: number, form: 1 | 2): Promise<CustomFieldListResponse> {
  const response = await apiClient.get<CustomFieldListResponse>(
    `${BASE}/config/custom-fields/${form}?schoolId=${schoolId}`
  );
  return response.data;
}

/**
 * Vue enrichie du drawer : champs V1 + attributs V2 EXISTANTS du rôle +
 * verdict par champ (réutilisation existant vs création). Nécessite un tenant
 * V2 déployé (409 sinon).
 */
async function getCustomFieldsV2Preview(
  schoolId: number,
  form: 1 | 2
): Promise<CustomFieldDrawerResponse> {
  const response = await apiClient.get<CustomFieldDrawerResponse>(
    `${BASE}/config/custom-fields/${form}/v2-preview?schoolId=${schoolId}`
  );
  return response.data;
}

/** Suggestion IA d'un `AttributeType` V2 pour un champ perso V1 (par sourceId). */
export interface CustomFieldAiSuggestion {
  sourceId: string;
  sourceLabel: string;
  targetCode: string | null;
  targetLabel: string | null;
  confidence: number;
}

export interface CustomFieldAiSuggestResponse {
  enabled: boolean;
  suggestions: CustomFieldAiSuggestion[];
}

/**
 * Pré-mapping IA (Gemini) : suggère un `AttributeType` V2 par champ perso V1
 * (par sourceId) d'un formulaire. `enabled:false` si la clé Gemini n'est pas
 * configurée. Suggestion pure : rien n'est persisté côté serveur.
 */
async function aiSuggestCustomFields(
  schoolId: number,
  form: 1 | 2
): Promise<CustomFieldAiSuggestResponse> {
  const response = await apiClient.post<CustomFieldAiSuggestResponse>(
    `${BASE}/config/custom-field-ai-suggest?schoolId=${schoolId}`,
    { form }
  );
  return response.data;
}

/**
 * Auto-application du pré-mapping IA des champs perso (2 formulaires) pour une
 * école : persiste les suggestions non nulles des champs encore non mappés.
 * Utilisé en fire-and-forget à la création du projet.
 */
async function applyCustomFieldAiMapping(
  schoolId: number
): Promise<{ enabled: boolean; applied: number; byForm: Record<string, number> }> {
  const response = await apiClient.post<{
    enabled: boolean;
    applied: number;
    byForm: Record<string, number>;
  }>(`${BASE}/config/custom-field-ai-apply?schoolId=${schoolId}`);
  return response.data;
}

export interface CustomFieldMappingStatsByForm {
  parents: { form: 2; total: number; mapped: number; error?: string };
  students: { form: 1; total: number; mapped: number; error?: string };
}

/**
 * Compteurs total/mapped pour les deux formulaires d'un projet, pour
 * alimenter la cellule MAPPING des lignes user sur la page projet.
 */
async function getProjectCustomFieldMapping(projectId: number): Promise<CustomFieldMappingStatsByForm> {
  const response = await apiClient.get<CustomFieldMappingStatsByForm>(
    `${BASE}/projects/${projectId}/custom-field-mapping`
  );
  return response.data;
}

async function seedRoles(): Promise<{ message: string; count: number }> {
  const response = await apiClient.post<{ message: string; count: number }>(`${BASE}/config/seed/roles`);
  return response.data;
}

/**
 * Initialise / rafraîchit le référentiel global des champs natifs `ef_*`
 * (commun à toutes les écoles). Idempotent.
 */
async function seedNativeFields(): Promise<{
  message: string;
  created: number;
  updated: number;
  legacyRemoved?: number;
}> {
  const response = await apiClient.post<{
    message: string;
    created: number;
    updated: number;
    legacyRemoved?: number;
  }>(`${BASE}/config/seed/native-fields`);
  return response.data;
}

// --- Manual Decisions (persistant, par école) ---

/**
 * Types de décisions manuelles persistantes standards.
 * Voir ManualDecisionService côté backend pour la structure de decisionValue.
 *
 * La cible V2 (tenant_group, tenant) n'est PAS gérée ici : elle provient
 * directement de SchoolDeployment (voir V2TargetStatusCard). Les décisions
 * ci-dessous couvrent les résolutions métier propres à la migration.
 */
export const DECISION_TYPES = {
  USER_DEDUP: 'user_dedup',
  MISSING_ROLE_MAPPING: 'missing_role_mapping',
} as const;

export type DecisionType = (typeof DECISION_TYPES)[keyof typeof DECISION_TYPES] | string;

export interface ManualDecision {
  id: number;
  schoolId: number;
  decisionType: DecisionType;
  decisionKey: string;
  decisionValue: Record<string, unknown> | null;
  notes: string | null;
  resolvedBy: { id: number; name: string } | null;
  resolvedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UpsertDecisionInput {
  decisionType: DecisionType;
  decisionKey?: string;
  decisionValue: Record<string, unknown> | null;
  notes?: string | null;
}

async function getSchoolDecisions(
  schoolId: number,
  type?: DecisionType
): Promise<ManualDecision[]> {
  const query = type ? `?type=${encodeURIComponent(type)}` : '';
  const response = await apiClient.get<ManualDecision[]>(
    `${BASE}/schools/${schoolId}/decisions${query}`
  );
  return response.data;
}

async function upsertSchoolDecision(
  schoolId: number,
  data: UpsertDecisionInput
): Promise<ManualDecision> {
  const response = await apiClient.put<ManualDecision>(
    `${BASE}/schools/${schoolId}/decisions`,
    data
  );
  return response.data;
}

async function deleteSchoolDecision(schoolId: number, decisionId: number): Promise<void> {
  await apiClient.delete(`${BASE}/schools/${schoolId}/decisions/${decisionId}`);
}

async function clearSchoolDecisions(schoolId: number): Promise<{ message: string; count: number }> {
  const response = await apiClient.delete<{ message: string; count: number }>(
    `${BASE}/schools/${schoolId}/decisions`
  );
  return response.data;
}

// --- Referential overrides (par école) ---

export interface ReferentialOverrideRow {
  sourceId: string;
  sourceLabel: string;
  targetCode: string | null;
  targetLabel: string | null;
  enCode: string | null;
  hasOverride: boolean;
  globalSourceLabel: string | null;
  globalTargetCode: string | null;
  globalTargetLabel: string | null;
  globalEnCode: string | null;
  v1LiveLabel: string | null;
  /** Niveaux V1 associés au libellé ("CP, CE1, …") — matières uniquement. */
  v1Levels: string | null;
  /** Niveaux uniquement : nom du cycle V1 associé (null si aucun). */
  v1CycleLabel?: string | null;
  /** Niveaux uniquement : cycle résolu (override `niveau_cycle` sinon cycle V1 mappé). */
  cycleCode?: string | null;
  cycleLabel?: string | null;
  cycleSource?: 'override' | 'v1' | null;
}

export interface ReferentialOverridesResponse {
  type: string;
  school: { id: number; code: string; name: string };
  mappings: ReferentialOverrideRow[];
  v1Live: { available: boolean; error: string | null };
}

export interface UpsertOverrideInput {
  type?: string;
  sourceId: string;
  sourceLabel?: string | null;
  targetCode?: string | null;
  targetLabel?: string | null;
  enCode?: string | null;
  /**
   * Contexte d'association (type `matiere_niveau` uniquement) : libellés bruts
   * matière/niveau + MEF du niveau. Permet au back d'alimenter la config
   * globale (composite + catalogues matière/niveau) en insert-si-absent, pour
   * que le choix devienne le défaut des autres écoles.
   */
  matiere?: string | null;
  niveau?: string | null;
  mef?: string | null;
}

/** Suggestion IA d'un targetCode pour un libellé V1 (drawer de mapping). */
export interface ReferentialAiSuggestion {
  sourceLabel: string;
  targetCode: string | null;
  targetLabel: string | null;
  confidence: number;
}

/**
 * Suggestion de code MEF pour un libellé V1 de niveau déjà mappé (déterministe
 * primaire/collège — MEF unique du niveau — ou IA pour les séries de lycée).
 */
export interface ReferentialMefSuggestion {
  sourceLabel: string;
  enCode: string;
  enLabel: string | null;
  confidence: number;
}

export interface ReferentialAiSuggestResponse {
  enabled: boolean;
  suggestions: ReferentialAiSuggestion[];
  /** Niveaux uniquement : MEF suggérés pour les lignes mappées sans MEF. */
  mefSuggestions?: ReferentialMefSuggestion[];
}

// --- Codes BCN par association matière × niveau ---
// La nomenclature BCN est définie par MEF : le code d'une matière se choisit
// par association (matière, niveau). Type référentiel `matiere_niveau`,
// clé composite portable entre écoles (libellés normalisés).

export interface MatiereNiveauAssociation {
  /** Clé composite `MATIERE||NIVEAU` (sourceId des overrides `matiere_niveau`). */
  key: string;
  matiere: string;
  niveau: string;
  /** MEF du niveau (mapping niveau ou level V2 du tenant), null si inconnu (ex. primaire). */
  mef: string | null;
  enCode: string | null;
  /** Libellé de la matière BCN du code courant (affichage « nom (code) » hors MEF). */
  enLabel?: string | null;
  /** Provenance du code : override école, défaut global composite, ou héritage du code matière. */
  source: 'override' | 'global' | 'matiere' | null;
}

export interface MatiereNiveauViewResponse {
  school: { id: number; code: string; name: string };
  associations: MatiereNiveauAssociation[];
  /** Options BCN par MEF (matières associables, data API `mef_matieres`). */
  mefOptions: Record<string, EnCodeOption[]>;
}

async function getMatiereNiveauView(schoolId: number): Promise<MatiereNiveauViewResponse> {
  const response = await apiClient.get<MatiereNiveauViewResponse>(
    `${BASE}/schools/${schoolId}/referential-overrides/matiere-niveau-view`
  );
  return response.data;
}

export interface MatiereNiveauAiSuggestion {
  key: string;
  /** Libellés de l'association (appariement UI robuste, indépendant de la clé normalisée). */
  matiere?: string;
  niveau?: string;
  enCode: string | null;
  enLabel: string | null;
  confidence: number;
}

async function aiSuggestMatiereNiveau(
  schoolId: number
): Promise<{ enabled: boolean; suggestions: MatiereNiveauAiSuggestion[] }> {
  const response = await apiClient.post<{ enabled: boolean; suggestions: MatiereNiveauAiSuggestion[] }>(
    `${BASE}/config/matiere-niveau-ai-suggest?schoolId=${schoolId}`,
    {}
  );
  return response.data;
}

// --- Référentiel global matière × niveau (défauts appliqués à toutes les écoles) ---

export interface MatiereNiveauGlobalView {
  /** Catalogue des libellés de matières (éditable dans l'onglet). */
  matieres: string[];
  /** Catalogue des niveaux (issu de l'onglet Niveaux), avec leur MEF. */
  niveaux: { label: string; mef: string | null }[];
  /** Matrice matière × niveau avec le code BCN résolu (défaut global composite). */
  associations: MatiereNiveauAssociation[];
  /** Options BCN par MEF (matières associables, data API `mef_matieres`). */
  mefOptions: Record<string, EnCodeOption[]>;
}

async function getMatiereNiveauGlobalView(): Promise<MatiereNiveauGlobalView> {
  const response = await apiClient.get<MatiereNiveauGlobalView>(`${BASE}/config/matiere-niveau-view`);
  return response.data;
}

async function saveMatiereNiveauGlobal(payload: {
  matieres: string[];
  associations: { matiere: string; niveau: string; enCode: string | null }[];
}): Promise<{ message: string }> {
  const response = await apiClient.put<{ message: string }>(
    `${BASE}/config/matiere-niveau`,
    payload
  );
  return response.data;
}

/**
 * Pré-mapping IA global des codes BCN par association matière × niveau. Le
 * corps porte le catalogue de matières en cours d'édition (pour couvrir les
 * matières non encore enregistrées).
 */
async function aiSuggestMatiereNiveauGlobal(
  matieres: string[]
): Promise<{ enabled: boolean; suggestions: MatiereNiveauAiSuggestion[] }> {
  const response = await apiClient.post<{ enabled: boolean; suggestions: MatiereNiveauAiSuggestion[] }>(
    `${BASE}/config/matiere-niveau-ai-suggest`,
    { matieres }
  );
  return response.data;
}

/** Recherche MEF (mapping niveau→MEF). Mutualise le service EN de l'assistant. */
async function searchEnMefs(query: string): Promise<EnCodeOption[]> {
  const q = query.trim();
  if (!q) return [];
  const response = await apiClient.get<{ items: EnCodeOption[] }>(
    `${BASE}/config/en/mefs?q=${encodeURIComponent(q)}`
  );
  return response.data.items ?? [];
}

/** Recherche matière (code BCN) pour le mapping matière→BCN. Enrichie LV. */
async function searchEnMatieres(query: string): Promise<EnCodeOption[]> {
  const q = query.trim();
  if (!q) return [];
  const response = await apiClient.get<{ items: EnCodeOption[] }>(
    `${BASE}/config/en/matieres?q=${encodeURIComponent(q)}`
  );
  return response.data.items ?? [];
}

async function getReferentialOverrides(
  schoolId: number,
  type: string = 'role',
  withV1Live: boolean = true
): Promise<ReferentialOverridesResponse> {
  const params = new URLSearchParams({ type });
  if (withV1Live) params.append('withV1Live', '1');
  const response = await apiClient.get<ReferentialOverridesResponse>(
    `${BASE}/schools/${schoolId}/referential-overrides?${params.toString()}`
  );
  return response.data;
}

async function upsertReferentialOverride(
  schoolId: number,
  input: UpsertOverrideInput
): Promise<unknown> {
  const response = await apiClient.put(
    `${BASE}/schools/${schoolId}/referential-overrides`,
    input
  );
  return response.data;
}

async function deleteReferentialOverride(
  schoolId: number,
  type: string,
  sourceId: string
): Promise<{ deleted: boolean }> {
  const response = await apiClient.delete<{ deleted: boolean }>(
    `${BASE}/schools/${schoolId}/referential-overrides/${type}/${encodeURIComponent(sourceId)}`
  );
  return response.data;
}

/**
 * Pré-mapping IA (Gemini) : suggère un targetCode par libellé V1 contre la
 * nomenclature V2. `enabled:false` si la clé Gemini n'est pas configurée.
 */
async function aiSuggestReferential(
  schoolId: number,
  type: string,
  sourceLabels: string[],
  /** Contexte optionnel `libellé V1 → niveaux associés` (matières). */
  sourceLevels?: Record<string, string>,
  /** Niveaux uniquement : lignes mappées sans MEF → suggestion de MEF. */
  mefTargets?: { sourceLabel: string; targetCode: string }[]
): Promise<ReferentialAiSuggestResponse> {
  const response = await apiClient.post<ReferentialAiSuggestResponse>(
    `${BASE}/config/referential-ai-suggest?schoolId=${schoolId}`,
    {
      type,
      sourceLabels,
      ...(sourceLevels ? { sourceLevels } : {}),
      ...(mefTargets && mefTargets.length > 0 ? { mefTargets } : {}),
    }
  );
  return response.data;
}

// --- V2 normalized data (proxy vers data.ecole-futee.com) ---

/**
 * Item d'une nomenclature `data.ecole-futee.com` (cycles, niveaux, mefs, …).
 * La forme exacte dépend de la ressource ; on garde un type souple.
 */
export interface V2DataItem {
  code?: string;
  libelle?: string;
  libelleLong?: string;
  libelleCourt?: string;
  label?: string;
  name?: string;
  /** Ressource `matieres` (BCN) : le code est porté par `matiereEnseignee`. */
  matiereEnseignee?: string;
  libelleLongMatiereEnseigne?: string;
  libelleCourtMatiereEnseigne?: string;
  libelleEdition?: string;
  [k: string]: unknown;
}

/**
 * Cache mémoire (durée de session) des nomenclatures V2 normalisées.
 *
 * Ces nomenclatures (`cycles`, `niveaux`, …) sont globales et très stables,
 * mais l'endpoint distant V2 est lent (plusieurs secondes sur cache froid).
 * On évite donc :
 *  - les re-fetch inutiles au changement d'onglet / re-mount (StrictMode) ;
 *  - les appels concurrents identiques, dédupliqués via la promesse en vol.
 */
const v2DataCache = new Map<string, V2DataItem[]>();
const v2DataInflight = new Map<string, Promise<V2DataItem[]>>();

/**
 * Charge une ressource de nomenclature V2 normalisée.
 *
 * `schoolId` est optionnel : sans école (page de config globale), le backend
 * choisit n'importe quel tenant V2 déployé comme contexte de référence.
 */
async function getV2DataResource(resource: string, schoolId?: number): Promise<V2DataItem[]> {
  const cacheKey = `${resource}:${schoolId ?? 'global'}`;

  const cached = v2DataCache.get(cacheKey);
  if (cached) return cached;

  const inflight = v2DataInflight.get(cacheKey);
  if (inflight) return inflight;

  const query = schoolId != null ? `?schoolId=${schoolId}` : '';
  const promise = apiClient
    .get<{ resource: string; items: V2DataItem[] }>(`${BASE}/config/v2-data/${resource}${query}`)
    .then((response) => {
      const items = response.data.items ?? [];
      v2DataCache.set(cacheKey, items);
      return items;
    })
    .finally(() => {
      v2DataInflight.delete(cacheKey);
    });

  v2DataInflight.set(cacheKey, promise);
  return promise;
}

// --- Export ---

export const migrationV1V2Api = {
  // Projects
  getProjects,
  getProject,
  createProject,
  deleteProject,
  analyzeProject,
  runProject,
  pauseProject,
  resumeProject,
  resetProject,
  restartFromScratch,
  repairCollapsedParents,
  repairDuplicateUsers,
  v1BillingLockdown,
  // Step definitions
  getStepDefinitions,
  // Step executions
  getStepExecution,
  updateStepExecutionMapping,
  runStepExecution,
  runStepChunk,
  runStepChunkResilient,
  isTransientChunkError,
  getSchedulesV2Status,
  purgeSchedulesV2,
  getFieldValueJobs,
  retryFieldValueJobs,
  skipStep,
  bypassStep,
  refreshStepCount,
  runNextStep,
  runAllSteps,
  // Records
  getRecords,
  retryRecord,
  updateRecordMappedData,
  excludeRecord,
  includeRecord,
  getRecordCorrectionSchema,
  // Fichiers migrés (lots « fichiers »)
  getStepFiles,
  fetchRecordV2FileObjectUrl,
  // Rapport d'audit
  getProjectReport,
  generateProjectReport,
  // Rattrapage automatique des utilisateurs
  getProjectAutoCatchUps,
  runProjectAutoCatchUp,
  runProjectBirthPlaceBackfill,
  getAutoCatchUpSweeps,
  getAutoCatchUpSweep,
  getAutoCatchUpSchools,
  getAutoCatchUpSchool,
  // Manual actions
  getManualActions,
  resolveAction,
  dismissAction,
  // Comments (fil de discussion projet)
  getComments,
  createComment,
  deleteComment,
  // Logs
  getLogs,
  // Project config (years)
  getSourceSchoolYears,
  getTargetSchoolYears,
  updateProjectConfig,
  refreshProjectCounts,
  refreshProjectSourceCounts,
  // Schools
  getSchools,
  // Board
  getBoard,
  assignProject,
  updateProjectPlannedDate,
  getAssignableUsers,
  // Config
  getMappings,
  saveMappings,
  seedRoles,
  seedNativeFields,
  // EN codes (mapping niveau→MEF / matière→BCN, mutualisé avec l'assistant)
  searchEnMefs,
  searchEnMatieres,
  // Codes BCN par association matière × niveau
  getMatiereNiveauView,
  aiSuggestMatiereNiveau,
  // Référentiel global matière × niveau
  getMatiereNiveauGlobalView,
  saveMatiereNiveauGlobal,
  aiSuggestMatiereNiveauGlobal,
  // Custom fields (V1 customfields → V2 AttributeType)
  getCustomFields,
  getCustomFieldsV2Preview,
  getProjectCustomFieldMapping,
  aiSuggestCustomFields,
  applyCustomFieldAiMapping,
  // Manual Decisions (persistant par école)
  getSchoolDecisions,
  upsertSchoolDecision,
  deleteSchoolDecision,
  clearSchoolDecisions,
  // Referential overrides (par école)
  getReferentialOverrides,
  upsertReferentialOverride,
  deleteReferentialOverride,
  aiSuggestReferential,
  // V2 normalized data (nomenclature proxy)
  getV2DataResource,
};
